import dayjs from "dayjs";
import html2pdf from "html2pdf.js";

interface DoctorInfo {
  name: string;
  specialty: string;
  email: string;
  avatar?: string;
  contact?: string;
  hospital?: string;
}

interface PatientInfo {
  name: string;
  age?: string;
  gender?: string;
  email?: string;
  contact?: string;
  mrn?: string;
}

interface ReferralData {
  id: string;
  date: string;
  reason: string;
  status: string;
  referredBy?: DoctorInfo;
  referredTo: DoctorInfo;
  patient?: PatientInfo;
  notes?: string;
  distance?: string;
  travelTime?: string;
  workingHours?: string;
  healthPlans?: string;
  location?: string;
  clinicalDetails?: ClinicalDetails;
}

interface ClinicalDetails {
  presentingComplaint?: string;
  historyOfPresentIllness?: string;
  pastMedicalHistory?: string;
  currentMedications?: string;
  allergies?: string;
  vitalSigns?: string;
  investigations?: string;
}

export const downloadReferralToPDF = (referral: ReferralData, patient?: PatientInfo, clinical?: ClinicalDetails) => {
  const patientData = patient || referral.patient;
  const clinicalData = clinical || referral.clinicalDetails;
  
  const element = document.createElement("div");
  element.innerHTML = `
    <div style="padding: 20px; font-family: Arial, sans-serif; color: #333;">
      <h1 style="text-align: center; color: #0A9CF5; margin-bottom: 30px; border-bottom: 2px solid #0A9CF5; padding-bottom: 10px;">REFERRAL ORDER</h1>
      
      <!-- Referring Doctor Information -->
      ${
        referral?.referredBy
          ? `
      <div style="margin-top: 20px; border: 1px solid #e0e0e0; padding: 15px; border-radius: 6px; page-break-inside: avoid;">
        <div style="background-color: #0A9CF5; padding: 10px; margin: -15px -15px 10px -15px; border-radius: 6px 6px 0 0;">
          <h3 style="color: white; margin: 0;">Referring Doctor Information</h3>
        </div>
        <table style="width: 100%; font-size: 12px;">
          <tr>
            <td style="padding: 8px; font-weight: bold; width: 50%;">Name</td>
            <td style="padding: 8px;">Dr. ${referral.referredBy.name || "N/A"}</td>
          </tr>
          <tr>
            <td style="padding: 8px; font-weight: bold;">Specialty</td>
            <td style="padding: 8px;">${referral.referredBy.specialty || "N/A"}</td>
          </tr>
          <tr>
            <td style="padding: 8px; font-weight: bold;">Contact</td>
            <td style="padding: 8px;">${referral.referredBy.contact || referral.referredBy.email || "N/A"}</td>
          </tr>
          ${
            referral.referredBy.hospital
              ? `<tr>
            <td style="padding: 8px; font-weight: bold;">Hospital/Clinic</td>
            <td style="padding: 8px;">${referral.referredBy.hospital}</td>
          </tr>`
              : ""
          }
        </table>
      </div>
      `
          : ""
      }

      <!-- Patient Information -->
      <div style="margin-top: 20px; border: 1px solid #e0e0e0; padding: 15px; border-radius: 6px; page-break-inside: avoid;">
        <div style="background-color: #0A9CF5; padding: 10px; margin: -15px -15px 10px -15px; border-radius: 6px 6px 0 0;">
          <h3 style="color: white; margin: 0;">Patient Information</h3>
        </div>
        <table style="width: 100%; font-size: 12px;">
          <tr>
            <td style="padding: 8px; font-weight: bold; width: 50%;">Name</td>
            <td style="padding: 8px;">${patientData?.name || "N/A"}</td>
          </tr>
          <tr>
            <td style="padding: 8px; font-weight: bold;">Age</td>
            <td style="padding: 8px;">${patientData?.age || "N/A"}</td>
            <td style="padding: 8px; font-weight: bold; width: 50%;">Gender</td>
            <td style="padding: 8px;">${patientData?.gender || "N/A"}</td>
          </tr>
          <tr>
            <td style="padding: 8px; font-weight: bold;">Contact</td>
            <td style="padding: 8px;">${patientData?.contact || "N/A"}</td>
            <td style="padding: 8px; font-weight: bold;">Medical Record (MRN)</td>
            <td style="padding: 8px;">${patientData?.mrn || "N/A"}</td>
          </tr>
          <tr>
            <td style="padding: 8px; font-weight: bold;">Email</td>
            <td style="padding: 8px; word-break: break-word;" colspan="3">${patientData?.email || "N/A"}</td>
          </tr>
        </table>
      </div>
      <div style="margin-top: 20px; border: 1px solid #e0e0e0; padding: 15px; border-radius: 6px; page-break-inside: avoid;">
        <div style="background-color: #0A9CF5; padding: 10px; margin: -15px -15px 10px -15px; border-radius: 6px 6px 0 0;">
          <h3 style="color: white; margin: 0;">Referral Doctor Information</h3>
        </div>
        <table style="width: 100%; font-size: 12px;">
          <tr>
            <td style="padding: 8px; font-weight: bold; width: 50%;">Name</td>
            <td style="padding: 8px;">Dr. ${referral.referredTo.name || "N/A"}</td>
          </tr>
          <tr>
            <td style="padding: 8px; font-weight: bold;">Specialty</td>
            <td style="padding: 8px;">${referral.referredTo.specialty || "N/A"}</td>
          </tr>
          <tr>
            <td style="padding: 8px; font-weight: bold;">Contact</td>
            <td style="padding: 8px;">${referral.referredTo.contact || referral.referredTo.email || "N/A"}</td>
          </tr>
          ${
            referral.referredTo.hospital
              ? `<tr>
            <td style="padding: 8px; font-weight: bold;">Hospital/Clinic</td>
            <td style="padding: 8px;">${referral.referredTo.hospital}</td>
          </tr>`
              : ""
          }
        </table>
      </div>

      <!-- Clinical Details -->
      ${
        clinicalData
          ? `
      <div style="margin-top: 20px; border: 1px solid #e0e0e0; padding: 15px; border-radius: 6px; page-break-inside: avoid;">
        <div style="background-color: #0A9CF5; padding: 10px; margin: -15px -15px 10px -15px; border-radius: 6px 6px 0 0;">
          <h3 style="color: white; margin: 0;">Clinical Details</h3>
        </div>
        
        ${
          clinicalData.presentingComplaint
            ? `
        <div style="margin-top: 10px;">
          <h4 style="color: #0A9CF5; margin: 0 0 5px 0; font-size: 12px;">Presenting Complaint</h4>
          <p style="font-size: 11px; margin: 0 0 10px 0; line-height: 1.4;">${clinicalData.presentingComplaint}</p>
        </div>
        `
            : ""
        }

        ${
          clinicalData.historyOfPresentIllness
            ? `
        <div style="margin-top: 10px;">
          <h4 style="color: #0A9CF5; margin: 0 0 5px 0; font-size: 12px;">History of Present Illness</h4>
          <p style="font-size: 11px; margin: 0 0 10px 0; line-height: 1.4;">${clinicalData.historyOfPresentIllness}</p>
        </div>
        `
            : ""
        }

        ${
          clinicalData.pastMedicalHistory
            ? `
        <div style="margin-top: 10px;">
          <h4 style="color: #0A9CF5; margin: 0 0 5px 0; font-size: 12px;">Past Medical History</h4>
          <p style="font-size: 11px; margin: 0 0 10px 0; line-height: 1.4;">${clinicalData.pastMedicalHistory}</p>
        </div>
        `
            : ""
        }

        ${
          clinicalData.currentMedications
            ? `
        <div style="margin-top: 10px;">
          <h4 style="color: #0A9CF5; margin: 0 0 5px 0; font-size: 12px;">Current Medications</h4>
          <p style="font-size: 11px; margin: 0 0 10px 0; line-height: 1.4;">${clinicalData.currentMedications}</p>
        </div>
        `
            : ""
        }

        ${
          clinicalData.allergies
            ? `
        <div style="margin-top: 10px;">
          <h4 style="color: #0A9CF5; margin: 0 0 5px 0; font-size: 12px;">Allergies</h4>
          <p style="font-size: 11px; margin: 0 0 10px 0; line-height: 1.4;">${clinicalData.allergies}</p>
        </div>
        `
            : ""
        }

        ${
          clinicalData.vitalSigns
            ? `
        <div style="margin-top: 10px;">
          <h4 style="color: #0A9CF5; margin: 0 0 5px 0; font-size: 12px;">Vital Signs</h4>
          <p style="font-size: 11px; margin: 0 0 10px 0; line-height: 1.4;">${clinicalData.vitalSigns}</p>
        </div>
        `
            : ""
        }

        ${
          clinicalData.investigations
            ? `
        <div style="margin-top: 10px;">
          <h4 style="color: #0A9CF5; margin: 0 0 5px 0; font-size: 12px;">Investigations</h4>
          <p style="font-size: 11px; margin: 0; line-height: 1.4; white-space: pre-wrap;">${clinicalData.investigations}</p>
        </div>
        `
            : ""
        }
      </div>
      `
          : ""
      }

      <!-- Referral Information -->
      <div style="margin-top: 20px; border: 1px solid #e0e0e0; padding: 15px; border-radius: 6px; page-break-inside: avoid;">
        <div style="background-color: #0A9CF5; padding: 10px; margin: -15px -15px 10px -15px; border-radius: 6px 6px 0 0;">
          <h3 style="color: white; margin: 0;">Reason for Referral</h3>
        </div>
        <table style="width: 100%; font-size: 12px;">
          <tr>
            <td style="padding: 8px; font-weight: bold; border-bottom: 1px solid #f0f0f0; width: 50%;">Reason</td>
            <td style="padding: 8px; border-bottom: 1px solid #f0f0f0;">${referral.reason || "N/A"}</td>
          </tr>
          <tr>
            <td style="padding: 8px; font-weight: bold; border-bottom: 1px solid #f0f0f0;">Date</td>
            <td style="padding: 8px; border-bottom: 1px solid #f0f0f0;">${dayjs(referral.date).format("DD-MMM-YYYY")}</td>
          </tr>
          <tr>
            <td style="padding: 8px; font-weight: bold; border-bottom: 1px solid #f0f0f0;">Status</td>
            <td style="padding: 8px; border-bottom: 1px solid #f0f0f0;">${referral.status || "N/A"}</td>
          </tr>
          ${
            referral.notes
              ? `
          <tr>
            <td style="padding: 8px; font-weight: bold;">Notes</td>
            <td style="padding: 8px;">${referral.notes}</td>
          </tr>
          `
              : ""
          }
        </table>
      </div>

      <!-- Signature Section -->
      <div style="margin-top: 30px; display: flex; justify-content: space-between;">
        <div style="width: 45%; text-align: center;">
          <div style="border-top: 1px solid #333; height: 40px; margin-bottom: 5px;"></div>
          <p style="font-size: 11px; font-weight: bold; margin: 0;">Signature</p>
        </div>
        <div style="width: 45%; text-align: center;">
          <p style="font-size: 11px; margin: 0;">${dayjs().format("DD-MMM-YYYY")}</p>
          <p style="font-size: 11px; font-weight: bold; margin: 0;">Date</p>
        </div>
      </div>

      <div style="margin-top: 30px; text-align: center; font-size: 10px; color: #999; border-top: 1px solid #e0e0e0; padding-top: 10px;">
        <p>Generated on ${dayjs().format("DD-MMM-YYYY HH:mm")}</p>
      </div>
    </div>
  `;

  const opt = {
    margin: 10,
    filename: `Referral_${referral.referredTo.name}_${dayjs().format("DD-MMM-YYYY")}.pdf`,
    image: { type: "jpeg" as const, quality: 0.98 },
    html2canvas: { scale: 2 },
    jsPDF: { orientation: "portrait" as const, unit: "mm" as const, format: "a4" as const },
  };

  html2pdf().set(opt).from(element).save();
};
